/**
 * Entity Registrations
 * 
 * This file registers all available entity types with their attributes, relations,
 * and data adapters. These entities can be used as data sources in templates.
 */

import React from 'react';
import { Contact, Users, Swords, Coins } from 'lucide-react';
import { EntityRegistry } from "./entityRegistry";
import {
  createTeamAdapter,
  createPlayerAdapter,
  createMatchAdapter,
  createSponsorAdapter,
} from "./entities.backend";

/**
 * Register all standard entity types
 */
export function registerEntities(registry: EntityRegistry): void {
  // Register Team entity
  registry.register({
    type: 'Team',
    displayName: 'Mannschaft',
    displayNamePlural: 'Mannschaften',
    attributes: [
      { 
        attributeKey: 'title', 
        attributeLabel: 'Mannschaftsname', 
        mediaType: 'text', 
        defaultValue: 'A-Junioren',
        required: true 
      }
    ],
    relations: [],
    adapterFactory: createTeamAdapter,
    labelGenerator: (entity: any) => {
      return entity?.title || 'Unbekannte Mannschaft';
    },
    ui: { 
      entitySelectComponent: 'EntitySelect/Team',
      icon: React.createElement(Users),
      color: 'blue'
    },
    meta: {
      description: 'Sports team entity with name, logo and basic information',
      tags: ['sports', 'team'],
      version: '1.0.0'
    }
  });

  // Register Match entity
  registry.register({
    type: 'Match',
    displayName: 'Spiel',
    displayNamePlural: 'Spiele',
    attributes: [
      { 
        attributeKey: 'opponentName', 
        attributeLabel: 'Gegner', 
        mediaType: 'text', 
        defaultValue: '1. FC Gegner',
        required: true 
      },
      { 
        attributeKey: 'date', 
        attributeLabel: 'Datum', 
        mediaType: 'date', 
        defaultValue: '2014-11-10T16:30:06.994Z',
        required: true 
      },
      { 
        attributeKey: 'location', 
        attributeLabel: 'Ort', 
        mediaType: 'text', 
        defaultValue: 'Waldplatz' 
      },
      { 
        attributeKey: 'leagueName', 
        attributeLabel: 'Liga', 
        mediaType: 'text', 
        defaultValue: 'Bezirksliga' 
      },
      { 
        attributeKey: 'ourScore', 
        attributeLabel: 'Unsere Tore', 
        mediaType: 'int', 
        defaultValue: 2,
        validation: { min: 0 }
      },
      { 
        attributeKey: 'opponentScore', 
        attributeLabel: 'Gegner Tore', 
        mediaType: 'int', 
        defaultValue: 1,
        validation: { min: 0 }
      },
      { 
        attributeKey: 'teamTitle', 
        attributeLabel: 'Mannschaftsname', 
        mediaType: 'text', 
        defaultValue: 'FC Musterhausen III' 
      },
      {
        attributeKey: 'homeOrAway',
        attributeLabel: 'Heim/Auswärts',
        mediaType: 'text',
        defaultValue: 'Auswärtsspiel'
      },
      {
        attributeKey: 'homeScore',
        attributeLabel: 'Heim Tore',
        mediaType: 'int',
        defaultValue: 2,
        validation: { min: 0 }
      },
      {
        attributeKey: 'homeName',
        attributeLabel: 'Heim Team',
        mediaType: 'text',
        defaultValue: 'FC Musterhausen III'
      },
      {
        attributeKey: 'homeLogo',
        attributeLabel: 'Heim Logo',
        mediaType: 'image',
        defaultValue: '/defaults/organization_placeholder.png'
      },
      {
        attributeKey: 'awayScore',
        attributeLabel: 'Auswärts Tore',
        mediaType: 'int',
        defaultValue: 1,
        validation: { min: 0 }
      },
      {
        attributeKey: 'awayName',
        attributeLabel: 'Auswärts Team',
        mediaType: 'text',
        defaultValue: '1. FC Gegner'
      },
      {
        attributeKey: 'awayLogo',
        attributeLabel: 'Auswärts Logo',
        mediaType: 'image',
        defaultValue: '/defaults/organization_placeholder.png'
      },
      {
        attributeKey: 'ourLogo',
        attributeLabel: 'Unser Logo',
        mediaType: 'image',
        defaultValue: '/defaults/organization_placeholder.png'
      },
      {
        attributeKey: 'opponentLogo',
        attributeLabel: 'Gegner Logo',
        mediaType: 'image',
        defaultValue: '/defaults/organization_placeholder.png'
      }
    ],
    relations: [],
    adapterFactory: createMatchAdapter,
    labelGenerator: (entity: any) => {
      const teamTitle = entity?.teamTitle || 'Unser Team';
      const opponentName = entity?.opponentName || 'Unbekannter Gegner';
      return `${teamTitle} vs ${opponentName}`;
    },
    ui: { 
      entitySelectComponent: 'EntitySelect/Match',
      icon: React.createElement(Swords),
      color: 'purple'
    },
    meta: {
      description: 'Spiel-Entität mit Backend-Integration für Post-Generator',
      tags: ['sports', 'match', 'event'],
      version: '1.0.0'
    }
  });

  // Register Player entity
  registry.register({
    type: 'Player',
    displayName: 'Spieler',
    displayNamePlural: 'Spieler',
    attributes: [
      { 
        attributeKey: 'firstName', 
        attributeLabel: 'Vorname', 
        mediaType: 'text', 
        defaultValue: 'Maximilian',
        required: true 
      },
      { 
        attributeKey: 'lastName', 
        attributeLabel: 'Nachname', 
        mediaType: 'text', 
        defaultValue: 'Mustermann',
        required: true 
      },
      { 
        attributeKey: 'fullName', 
        attributeLabel: 'Voller Name', 
        mediaType: 'text', 
        defaultValue: 'Maximilian Mustermann',
        required: true 
      },
      { 
        attributeKey: 'nickname', 
        attributeLabel: 'Spitzname', 
        mediaType: 'text', 
        defaultValue: 'Maxi' 
      },
      { 
        attributeKey: 'position', 
        attributeLabel: 'Position', 
        mediaType: 'text', 
        defaultValue: 'Stürmer' 
      },
      { 
        attributeKey: 'jerseyNumber', 
        attributeLabel: 'Trikotnummer', 
        mediaType: 'int', 
        defaultValue: 9,
        validation: { min: 1, max: 99 }
      },
      { 
        attributeKey: 'birthDate', 
        attributeLabel: 'Geburtsdatum', 
        mediaType: 'date', 
        defaultValue: '2000-01-01' 
      },
      { 
        attributeKey: 'nationality', 
        attributeLabel: 'Nationalität', 
        mediaType: 'text', 
        defaultValue: 'Deutschland' 
      },
      { 
        attributeKey: 'height', 
        attributeLabel: 'Größe (cm)', 
        mediaType: 'float', 
        defaultValue: 180 
      },
      { 
        attributeKey: 'weight', 
        attributeLabel: 'Gewicht (kg)', 
        mediaType: 'float', 
        defaultValue: 75 
      },
      { 
        attributeKey: 'biography', 
        attributeLabel: 'Biografie', 
        mediaType: 'text', 
        defaultValue: 'Max Mustermann ist ein talentierter Fußballspieler aus Deutschland.' 
      },
      { 
        attributeKey: 'imageUrl', 
        attributeLabel: 'Spielerbild', 
        mediaType: 'image', 
        defaultValue: '/defaults/player_placeholder.png' 
      },
      { 
        attributeKey: 'teamTitle', 
        attributeLabel: 'Mannschaftsname', 
        mediaType: 'text', 
        defaultValue: 'A-Junioren' 
      }
    ],
    relations: [
      { key: 'team', label: 'Team', target: 'Team' },
    ],
    adapterFactory: createPlayerAdapter,
    labelGenerator: (entity: any) => {
      // Prefer fullName if available, otherwise combine firstName and lastName
      if (entity?.fullName) {
        return entity.fullName;
      }
      const firstName = entity?.firstName || '';
      const lastName = entity?.lastName || '';
      if (firstName && lastName) {
        return `${firstName} ${lastName}`;
      }
      return firstName || lastName || 'Unbekannter Spieler';
    },
    ui: { 
      entitySelectComponent: 'EntitySelect/Player',
      icon: React.createElement(Contact),
      color: 'orange'
    },
    meta: {
      description: 'Spieler-Entität mit Backend-Integration für Post-Generator',
      tags: ['sports', 'player', 'person'],
      version: '1.0.0'
    }
  });

  // Register Sponsor entity
  registry.register({
    type: 'Sponsor',
    displayName: 'Sponsor',
    displayNamePlural: 'Sponsoren',
    attributes: [
      { 
        attributeKey: 'name', 
        attributeLabel: 'Name', 
        mediaType: 'text', 
        defaultValue: 'Sponsor GmbH',
        required: true 
      },
      { 
        attributeKey: 'logoUrl', 
        attributeLabel: 'Logo', 
        mediaType: 'image', 
        defaultValue: '/defaults/sponsor_placeholder.png' 
      },
      { 
        attributeKey: 'primaryColor', 
        attributeLabel: 'Hauptfarbe', 
        mediaType: 'color', 
        defaultValue: '#0066CC' 
      },
    ],
    relations: [],
    adapterFactory: createSponsorAdapter,
    labelGenerator: (entity: any) => {
      return entity?.name || 'Unbekannter Sponsor';
    },
    ui: { 
      entitySelectComponent: 'EntitySelect/Sponsor',
      icon: React.createElement(Coins),
      color: 'yellow'
    },
    meta: {
      description: 'Sponsor entity with branding information',
      tags: ['business', 'sponsor', 'branding'],
      version: '1.0.0'
    }
  });
}
